import type { EnumValueCache } from '@/types/electron';

/**
 * Build a key -> label map from one enum-value-cache entry.
 *
 * Alignment: `labels[i]` corresponds to `values[i]`. Whether an index is
 * PRESENT in the returned map matters as much as its value:
 *
 *  - present + `null`   — the target row exists and its label column value
 *                          was NULL (a genuine, known "no label").
 *  - ABSENT entirely     — unknown: `labels` didn't reach this index (a
 *                          shorter/partial array, e.g. a stale or
 *                          not-yet-refreshed cache). Indices at or beyond
 *                          `labels.length` are NOT added to the map.
 *
 * Callers that need to distinguish the two should use `.has(key)` (or check
 * `.get(key) === undefined`) rather than treating a falsy `.get()` result as
 * "no label" — `null` is a legitimate, present value.
 *
 * Returns an empty map when:
 *  - `cache` is undefined,
 *  - `cache.path` is a non-empty string — JSON-path cache entries are
 *    excluded; grid label rendering is column-level only,
 *  - `cache.labels` is absent — a non-lookup cache entry never carries
 *    labels.
 */
export function buildLookupLabelMap(
  cache: Pick<EnumValueCache, 'values' | 'labels' | 'path'> | undefined,
): Map<string, string | null> {
  const map = new Map<string, string | null>();
  if (!cache) return map;
  if (cache.path) return map;
  const { values, labels } = cache;
  if (!labels) return map;
  const n = Math.min(values.length, labels.length);
  for (let i = 0; i < n; i++) {
    map.set(values[i], labels[i]);
  }
  return map;
}
